import React, {useState, useEffect, useRef} from "react";
import {useVsiAppContext} from "../../../stores/VsiApp";
import {Card, Spinner} from "reactstrap";
import {VsiBubble, VsiH4, VsiH6, VsiMedLabel, VsiSpanText} from "../../../styled/App.styled";
import Grid from "../../../components/Grid";
import Highcharts from "highcharts/highstock";
import HighchartsReact from "highcharts-react-official";
import themeStyles from "../../../utils/themeStyles";

import {useDataWithRoute, useSwitchData, useTitleFromComponent} from "../../../utils/hooks";
import {topologyColors} from "../../../utils/chartUtils";
import {switchTopology} from "../../../utils/gridColumnDefs/switches/switchTopology";
import VsiLabeledSelect from "../../../components/VsiLabeledSelect";
import {hostImage, portImage, storageDeviceImage, switchErrors} from "../../../utils/utils";

import switchPortBlack from "../../../assets/images/switchPortBlack.svg";
import switchPortWhite from "../../../assets/images/switchPortWhite.svg";
import switchPortBlue from "../../../assets/images/switchPortBlue.svg";
import switchPortGreen from "../../../assets/images/switchPortGreen.svg";
import switchPortOrange from "../../../assets/images/switchPortOrange.svg";
import switchPortPurple from "../../../assets/images/switchPortPurple.svg";
import switchPortRed from "../../../assets/images/switchPortRed.svg";

import {capitalizeWord} from "../../../utils/Helpers";

require("highcharts/modules/networkgraph")(Highcharts);
require("highcharts/modules/accessibility")(Highcharts);
require("highcharts/modules/exporting")(Highcharts);

const topologyMetrics = [
    {
        value: 'storage',
        label: 'Storage',
        field: 'device_link_to_switch',
        fieldPrefix: 'device',
        color: topologyColors[1]
    },
    {
        value: 'switch',
        label: 'Switch',
        field: 'switch_name',
        fieldPrefix: 'switch',
        color: topologyColors[0]
    },
    {
        value: 'host',
        label: 'Host',
        field: 'host_name',
        fieldPrefix: 'host',
        color: topologyColors[2]
    },
];

const errorTypes = ["normal", "buffer_credit", "link_failure", "link_reset", "crc_errors", "loss_of_sync_signal"];
export default function SwitchTopology({context, title}) {
    useTitleFromComponent(`${context} / ${title}`);
    const { theme, client, periodId } = useVsiAppContext();

    const [topologyType, setTopologyType] = useState(topologyMetrics[0]);
    const [detailType, setDetailType] = useState(topologyMetrics[1]);

    const [topologyOptions, setTopologyOptions] = useState([]);
    const [detailOptions, setDetailOptions] = useState([]);
    const [topologyOption, setTopologyOption] = useState(null);
    const [detailOption, setDetailOption] = useState(null);

    const [isFormatting, setIsFormatting] = useState(true);

    const [filteredData, setFilteredData] = useState([]);

    const allRens = useRef({});

    let switchSGVs = [];

    const switchColorKey = {
        normal: theme === 'dark' ? switchPortWhite : switchPortBlack,
        buffer_credit: switchPortOrange,
        // link_failure: switchPortGreen,
        link_failure: switchPortRed,
        link_reset: switchPortBlue,
        crc_errors: switchPortPurple,
        loss_of_sync_signal: switchPortRed,
        red: switchPortRed
    }


    const [{recordset: optList}, {
        isLoading,
    }] = useDataWithRoute(`/api/v6/report/host_switch_topology_list_proc?client_id=${client.clientId}&period_id=${periodId}`);

    const [{recordset: data}, {
        isLoading: isDataLoading,
        error,
    }] = useSwitchData(`/api/v6/report/enterprise_switch_topology?client_id=${client.clientId}&period_id=${periodId}&topology_type=${topologyType?.field}&topology_value=${topologyOption?.value}`,
        Boolean(topologyOption && topologyType));


    useEffect(() => {
        setIsFormatting(true);
        const topologyOptions = topologyType.value === 'storage' ? [...new Set(optList.map(rec => rec.device_link_to_switch))] :
            topologyType.value === 'switch' ? [...new Set(optList.map(rec => rec.switch_name))] :
                [...new Set(optList.map(rec => rec.host_name))];

        setTopologyOptions(topologyOptions.map((o) => {
            return {label: o, value: o};
        }));
        setTopologyOption({label: topologyOptions[0], value: topologyOptions[0]});
    }, [optList, theme, topologyType]);


    useEffect(() => {
        setIsFormatting(false);
        populateDetailsOptions(detailType, data);
    }, [data]);

    const populateDetailsOptions = (DetailType, Data) => {
        const detailOptions = DetailType.value === 'storage' ? [...new Set(Data.map(rec => rec.device_link_to_switch))] :
            DetailType.value === 'switch' ? [...new Set(Data.map(rec => rec.switch_name))] :
                [...new Set(Data.map(rec => rec.host_name))];

        setDetailOptions(detailOptions.map((o) => {
            return {label: o, value: o};
        }));
        setDetailOption({label: detailOptions[0], value: detailOptions[0]});
        filterData(DetailType.value, detailOptions[0]);
    }

    const filterData = (DetailType, DetailOption) => {
        if (DetailType && DetailOption) {
            const newData = data
                .filter(obj => (DetailType === 'storage' ? obj.device_link_to_switch : DetailType === 'switch' ?
                    obj.switch_name : obj.host_name) === DetailOption);
            setFilteredData(newData);
        }
    }

    useEffect(() => {
        loadChart(filteredData);
    }, [filteredData])

    const [chartOptions, setChartOptions] = useState({
        annotations:
            [{
                shapes: [{
                    type: 'path',
                    fill: 'rgba(255,0,0,0.75)',
                    points: [{
                        xAxis: 50,
                        yAxis: 200
                    }, {
                        xAxis: 100,
                        yAxis: 200
                    }],
                }]
            }],
        mapNavigation: {
            enabled: false
        },
        chart: {
            type: "networkgraph",
            marginTop: 80,
            backgroundColor: themeStyles[theme].cardBgColor,
            height: 500,
        },
        credits: {
            enabled: false,
        },
        title: {
            text: ``,
            style: {
                color: themeStyles[theme].textColor
            }
        },
        subtitle: {
            text: '',
            style: {
                color: themeStyles[theme].textColor
            }
        },
        tooltip: {
            enabled: true,
            style: {
                color: "#000000"
            },
            formatter: function () {
                return "<b>Name: " + this.point.name + "</b><br/>" +
                    "<b>Zone: " + this.point.options.zone + "</b>";
            }
        },
        verticalAlign: "top",
        plotOptions: {
            networkgraph: {
                keys: ["from", "to"],
                layoutAlgorithm: {
                    enableSimulation: false,
                    theta: 0.5
                }
            }
        },
        series: [{nodes: []}]
    });


    const onTypeChange = (e, dropdown) => {
        if (dropdown === 'topology') {
            setTopologyOption(null);
            setTopologyType(e);
        } else if (dropdown === 'details') {
            populateDetailsOptions(e, data);
            setDetailType(e);
        }
    }
    const sleep = (sec) => {
        return new Promise(r => setTimeout(r, sec * 1000));
    }
    const onTopologyOptionChange = (e) => {
        setTopologyOption(e);
    }

    const onDetailOptionChange = (e) => {
        setDetailOption(e);
        filterData(detailType.value, e.value);
    }

    useEffect(() => {
        if (error) setIsFormatting(false);
    }, [error]);

    const loadFunction = function (chart) {
        // Pick particular node from nodes array:
        Object.keys(allRens.current).forEach(rect => {
            if (allRens.current[rect] !== undefined) allRens.current[rect].destroy();
            allRens.current[rect] = undefined;
        });
        const chartHeight = chart.plotHeight;

        const HostNodes = [];
        const StorageNodes = [];
        const PortNodes = [];
        const SwitchNodes = [];

        const switchesWithPorts = {};

        chart?.series?.[0]?.nodes?.forEach((node) => {
            if (node.category === "HOST") {
                HostNodes.push(node);
            } else if (node.category === "STORAGE") {
                StorageNodes.push(node);
            } else if (node.category === "PORT") {
                PortNodes.push(node);
                if (!switchesWithPorts.hasOwnProperty(node.switch_name)) {
                    switchesWithPorts[node.switch_name] = {
                        ports: [],
                    }
                }
                switchesWithPorts[node.switch_name].ports.push(node);

                if (SwitchNodes.indexOf(node.switch_name) < 0) {
                    SwitchNodes.push(node.switch_name);
                }
            }
        });

        const hostLimit = Math.floor((chart.plotWidth - 100) / HostNodes.length);
        HostNodes.forEach((host, index) => {
            const hostPosX = (chart.plotWidth / 2) + (hostLimit * index);
            host.fixedPosition = {
                x: hostPosX,
                y: Math.floor(chartHeight / 10) * 9
            };
            host.plotX = hostPosX;
            host.plotY = Math.floor(chartHeight / 10) * 9;
        });

        StorageNodes.forEach(device => {
            device.fixedPosition = {
                y: Math.floor(chartHeight / 10)
            };
            device.plotY = Math.floor(chartHeight / 10);
        });
        SwitchNodes?.forEach((s, idx) => {
            const PortSwitchNodes = [...switchesWithPorts[s].ports].sort((a, b) => parseInt(a.name.replace(/\D/g, "")) - parseInt(b.name.replace(/\D/g, "")));
            const portNum = PortSwitchNodes.length;
            let portRowLimit = 8;
            let switchXSpace = portRowLimit;
            let switchWidthSpace = 500;
            let rowSpace = 60;
            let colSpace = 40;
            let portsPerCol = 4;
            while (Math.floor(portNum / switchXSpace) > portsPerCol) {
                switchXSpace += switchXSpace;
                switchWidthSpace += switchWidthSpace;
                portsPerCol++;
            }

            const totalWidth = SwitchNodes.length * (switchWidthSpace + rowSpace) - rowSpace;
            const xPos = ((chart.plotWidth - totalWidth) / 2) + idx * (switchWidthSpace + rowSpace);

            allRens.current[`rect_${s}_${idx}`] =
                chart.renderer.rect(xPos, Math.floor(chartHeight / 10) * 5, switchWidthSpace, (portsPerCol * colSpace) + Math.floor(colSpace * .75), 5, 2)
                    .attr({
                        stroke: 'yellow',
                        // fill: 'grey',
                        zIndex: -10
                    })
                    .add();

            allRens.current[`label_${s}_${idx}`] =
                chart.renderer.label(s, xPos, Math.floor(chartHeight / 10) * 5)
                    .attr({
                        // stroke: 'red',
                        fill: 'yellow',
                        zIndex: -10
                    })
                    .add();

            PortSwitchNodes?.forEach((port, pidx) => {
                const nodeXPos = ((pidx % switchXSpace) * rowSpace) + xPos + 20;
                const nodeYPos = 20 + (colSpace) + colSpace * Math.floor(pidx / switchXSpace) + ((Math.floor(chartHeight / 10) * 4) / 2) + (colSpace / 2);
                port.fixedPosition = {
                    x: nodeXPos,
                    y: nodeYPos
                };
                port.plotX = nodeXPos;
                port.plotY = nodeYPos;
            })
        });
    };
    const connectionExists = ((lines, con2) => {
        return lines.find(con1 => con1["from"] === con2[0] && con1["to"] === con2[1]);
    });

    const connExistsBetweenTwoPoints = ((lines, a, b) => {
        return lines.find(con1 => con1["from"] === a && con1["to"] === b);
    });
    const nodeExists = (nodes, node) => {
        return nodes.find(n => n["id"] === node);
    };

    const returnLoadDraw = () => {
        return function () {
            loadFunction(this, topologyType.value, detailType.value);
        }
    }
    const loadChart = (data) => {
        let tmpChartData = [];
        let colors = Highcharts.getOptions().colors;
        let color_idx = 0;
        let tmpChartNodes = [];
        let hostPortConections = [];
        let devicePortConnections = [];
        const uniqueDevicePorts = {};

        data.forEach(d => {
            const conHP = [d["host_name"], d["host_port_name"], d["switch_name"], d["zone_name"], d['host_port_link_failure_str'], d["storage_port_link_failure"]];
            hostPortConections.push(conHP);
            const conDP = [d["switch_name"], d["storage_port_name"], d["device_link_to_switch"], d["zone_name"], d['storage_port_link_failure_str'], d["host_port_link_failure"]];
            devicePortConnections.push(conDP);
            uniqueDevicePorts[d["storage_port_name"]] = true;
            uniqueDevicePorts[d["host_port_name"]] = true;
        });

        hostPortConections.forEach(d => {
            if (!connectionExists(tmpChartData, d)) {
                const [hostName, hostPortName, switchName, zoneName, linkFailureStr, linkFailures] = d;

                if ((topologyType.value === "host" || detailType.value === "host") && !nodeExists(tmpChartNodes, hostName)) {
                    tmpChartNodes.push({
                        id: hostName,
                        category: 'HOST',
                        color: colors[color_idx],
                        marker: {
                            radius: 60,
                            symbol: `url(${hostImage})`,
                            height: 60
                        },
                        zone: zoneName,
                    });
                }
                if (hostPortName && hostPortName !== '') {
                    if (!nodeExists(tmpChartNodes, hostPortName)) {
                        const switchPortSvg = switchColorKey[linkFailureStr?.replace('host_', '') || 'normal'];
                        tmpChartNodes.push({
                            id: `${switchName}_${hostPortName}`,
                            name: hostPortName,
                            category: 'PORT',
                            switch_name: switchName,
                            color: colors[color_idx],
                            marker: {
                                radius: 25,
                                symbol: `url(${switchPortSvg})`
                            },
                            zone: zoneName,
                            link_failure: linkFailures
                        });
                    }
                }
                if ((topologyType.value === "host" || detailType.value === "host")) {
                    tmpChartData.push({from: hostName, to: `${switchName}_${hostPortName}`, zone: zoneName});
                }
            }
        });
        color_idx++;

        devicePortConnections.forEach(d => {
            const [switchName, storagePortName, deviceLinkToSwitch, zoneName, linkFailureStr, linkFailures] = d;

            // When select host-to-storage, if a host already connects to same port name as storage is about to, skip it.
            const avoidExistHostPort = hostPortConections.filter(array => array[1] === storagePortName);
            if (avoidExistHostPort.length > 0 && detailType.value !== "switch" && topologyType.value !== "switch") {
                return;
            }

            const switchPortSvg = switchColorKey[linkFailureStr?.replace('storage_', '') || 'normal'];
            tmpChartNodes.push({
                id: `${switchName}_${storagePortName}`,
                name: storagePortName,
                category: 'PORT',
                color: colors[color_idx],
                switch_name: switchName,
                marker: {
                    radius: 25,
                    symbol: `url(${switchPortSvg})`
                },
                zone: zoneName,
                link_failure: linkFailures
            });

            if ((topologyType.value === "storage" || detailType.value === "storage")) {
                if (!nodeExists(tmpChartNodes, deviceLinkToSwitch)) {
                    tmpChartNodes.push({
                        id: deviceLinkToSwitch,
                        category: 'STORAGE',
                        marker: {
                            symbol: `url(${storageDeviceImage})`,
                            radius: 300
                        },
                        zone: zoneName
                    });
                }

                if (!connExistsBetweenTwoPoints(tmpChartData, deviceLinkToSwitch, `${switchName}_${storagePortName}`)) {
                    tmpChartData.push({
                        from: deviceLinkToSwitch,
                        to: `${switchName}_${storagePortName}`,
                        zone: zoneName
                    });
                }
            }

        });

        setChartOptions({
            ...chartOptions,
            chart: {
                ...chartOptions.chart,
                backgroundColor: themeStyles[theme].cardBgColor,
                events: {
                    load: returnLoadDraw(),
                    redraw: returnLoadDraw(),
                }
            },
            title: {
                text: detailOption?.value ? `${topologyOption?.value ?? ''} ${capitalizeWord(topologyType.value)} Topology of ${capitalizeWord(detailType.value)} ${detailOption?.value ?? ''}` : '',
                style: {
                    color: themeStyles[theme].textColor
                }
            },
            series: [{
                id: "HostConnectedSwitches",
                // draggable: true,
                dataLabels: {
                    color: themeStyles[theme].textColor,
                    enabled: true,
                    linkFormat: "",
                    allowOverlap: false,
                    // style: {
                    //     textOutline: true
                    // }
                },
                nodes: tmpChartNodes,
                data: tmpChartData?.filter?.((v, i, a) => {
                    return a.findIndex(v2 => ['to', 'from', 'zone'].every(k => v2[k] === v[k])) === i
                })
            }]
        });
        setIsFormatting(false);
    };

    const filterChanged = (eventData) => {
        setIsFormatting(true);
        let rowData = [];
        eventData.api.forEachNodeAfterFilter(node => {
            rowData.push(node.data);
        });
        loadChart(rowData);
        setIsFormatting(false);
    };

    return <Card className={`report-card p-2 nav-card-${theme}`}>
        <VsiH4>Switch Topology</VsiH4>
        <VsiBubble noAutobot>
            <div className={"w-100 p-2 d-flex"}>
                <VsiLabeledSelect options={topologyMetrics.filter(metric => metric?.value !== detailType?.value)}
                                  isLoading={isFormatting || isLoading || isDataLoading}
                                  isDisabled={isFormatting || isLoading || isDataLoading}
                                  label={'Starting Topology'}
                                  onChange={e => onTypeChange(e, 'topology')}
                                  defaultValue={topologyType}/>
                <VsiLabeledSelect options={topologyMetrics.filter(metric => metric?.value !== topologyType?.value)}
                                  className={"ms-2"}
                                  isLoading={isFormatting || isLoading || isDataLoading}
                                  isDisabled={isFormatting || isLoading || isDataLoading}
                                  label={'Detail View'}
                                  onChange={e => onTypeChange(e, 'details')}
                                  defaultValue={detailType}/>
                <VsiLabeledSelect options={topologyOptions}
                                  isLoading={isFormatting || isLoading || isDataLoading}
                                  isDisabled={isFormatting || isLoading || isDataLoading}
                                  minWidth={250}
                                  label={topologyType.label}
                                  onChange={e => onTopologyOptionChange(e)}
                                  defaultValue={topologyOption}/>
                <VsiLabeledSelect options={detailOptions}
                                  isLoading={isFormatting || isLoading || isDataLoading}
                                  isDisabled={isFormatting || isLoading || isDataLoading}
                                  label={detailType.label}
                                  minWidth={250}
                                  onChange={e => onDetailOptionChange(e)}
                                  defaultValue={detailOption}/>
            </div>
        </VsiBubble>
        {/* <VsiBubble autobotName='SwitchTopologyChart' unit={topologyType?.value} autobotInfo={{ secondaryMetric: [detailType?.value, topologyOption?.value, detailOption?.value] }}> */}
        <VsiBubble noAutobot>
            {isFormatting || isLoading || isDataLoading ? (
                <div style={{height: 500}} className={"d-flex justify-content-center align-items-center"}>
                    <Spinner color={"success"}/>
                </div>
            ) : (
                <>
                    {filteredData.length === 0 ?
                        <div style={{height: 500}} className={"d-flex justify-content-center align-items-center"}>
                            <VsiSpanText>No data</VsiSpanText>
                        </div> :
                        <HighchartsReact highcharts={Highcharts} options={chartOptions}/>
                    }
                </>

            )}
            <VsiH6>Legend:</VsiH6>
            <div className={"d-flex flex-row"}>
                <div className={"d-flex flex-column align-items-center justify-content-center pe-3"}>
                    <img alt={'Storage Device Icon'} src={storageDeviceImage}/>
                    <VsiMedLabel>
                        Storage Device
                    </VsiMedLabel>
                </div>
                <div className={"d-flex flex-column align-items-center justify-content-center pe-3"}>
                    <div style={{
                        border: 'solid yellow 2px',
                        padding: '20px 10px 10px 20px',
                        height: 60,
                        width: 50,
                        borderRadius: '5px'
                    }}>

                    </div>
                    <VsiMedLabel>
                        Switch
                    </VsiMedLabel>
                </div>
                <div className={"d-flex flex-column align-items-center justify-content-center pe-3"}>
                    <img alt={'Host Icon'} src={hostImage}/>
                    <VsiMedLabel>
                        Host
                    </VsiMedLabel>
                </div>

            </div>
        </VsiBubble>
        <VsiBubble unit={topologyType?.value}
                   autobotInfo={{secondaryMetric: [detailType?.value, topologyOption?.value, detailOption?.value]}}>
            <Grid data={filteredData}
                  columnDefs={switchTopology}
                  filterChanged={filterChanged}/>
        </VsiBubble>
    </Card>
}
